/**
 * Date Service
 * Centralized utility functions for date handling across the application
 */
import { Timestamp } from 'firebase/firestore';
import { logger } from './loggerService';

/** Milliseconds in one hour */
export const MS_PER_HOUR = 1000 * 60 * 60;

/** Milliseconds in one day */
export const MS_PER_DAY = MS_PER_HOUR * 24;

/**
 * Parse a date input safely, handling timezone issues with date-only strings.
 *
 * IMPORTANT: When JavaScript parses "YYYY-MM-DD" strings, it interprets them as UTC midnight.
 * For users in timezones west of UTC (like US), this causes dates to appear as the previous day.
 * This function ensures date-only strings are parsed as local midnight instead.
 *
 * @param {Date|Timestamp|String|Number} dateInput - The date to parse
 * @returns {Date|null} Parsed Date object or null if invalid
 */
export function parseLocalDate(dateInput) {
  try {
    if (!dateInput) return null;

    // Already a Date object
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }

    // Firestore Timestamp (with toDate method)
    if (dateInput?.toDate) {
      return dateInput.toDate();
    }

    // Firestore Timestamp-like object with _seconds
    if (dateInput?._seconds !== undefined) {
      return new Date(dateInput._seconds * 1000);
    }

    // Number (timestamp in milliseconds)
    if (typeof dateInput === 'number') {
      const date = new Date(dateInput);
      return isNaN(date.getTime()) ? null : date;
    }

    // String handling
    if (typeof dateInput === 'string') {
      // Date-only string (YYYY-MM-DD): parse as LOCAL midnight to avoid timezone shift
      if (dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const date = new Date(dateInput + 'T00:00:00');
        return isNaN(date.getTime()) ? null : date;
      }

      // ISO string with time component or other formats: parse normally
      const date = new Date(dateInput);
      return isNaN(date.getTime()) ? null : date;
    }

    logger.warn('[DateService] parseLocalDate: Unrecognized date input type', {
      dateInput,
      type: typeof dateInput
    });
    return null;
  } catch (error) {
    logger.error('[DateService] parseLocalDate: Error parsing date', {
      error: error.message,
      dateInput
    });
    return null;
  }
}

/**
 * Convert Firebase Timestamp to ISO string (YYYY-MM-DD)
 * @param {Timestamp|Date} timestamp 
 * @returns {String} ISO date string
 */
export function convertTimestampToISO(timestamp) {
  try {
    let date;
    
    // Check if it's a Firestore Timestamp-like object with _seconds
    if (timestamp?._seconds !== undefined) {
      date = new Date(timestamp._seconds * 1000);
      logger.debug('[DateService] Converting Firestore timestamp with _seconds', { 
        seconds: timestamp._seconds,
        date: date.toISOString()
      });
    } else if (timestamp instanceof Timestamp || timestamp?.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      logger.warn('[DateService] Invalid timestamp provided', { 
        timestamp,
        type: typeof timestamp,
        hasSeconds: timestamp?._seconds !== undefined
      });
      return null;
    }
    
    // Format as YYYY-MM-DD in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    logger.error('[DateService] Error converting timestamp to ISO:', {
      error: error.message,
      timestamp
    });
    return null;
  }
}

/**
 * Convert ISO string or Date to Firebase Timestamp with time preserved
 * Does NOT normalize to noon - preserves the actual time
 * @param {String|Date} dateValue 
 * @returns {Timestamp} Firebase Timestamp
 */
export function convertToTimestampWithTime(dateValue) {
  try {
    if (!dateValue) return null;

    // Already a timestamp - return as is
    if (dateValue instanceof Timestamp || dateValue?.toDate) {
      return dateValue;
    }

    // Use parseLocalDate for safe date parsing (handles timezone issues)
    const date = parseLocalDate(dateValue);

    if (!date) {
      logger.warn('[DateService] Invalid date value provided to convertToTimestampWithTime', { dateValue });
      return null;
    }

    // Return timestamp with actual time preserved (no normalization)
    return Timestamp.fromDate(date);
  } catch (error) {
    logger.error('[DateService] Error converting to timestamp with time:', {
      error: error.message,
      dateValue
    });
    return null;
  }
}

/**
 * Format date for display
 * @param {Date|Timestamp|String} date 
 * @param {String} format - 'short', 'long', 'full', or custom options object
 * @returns {String} Formatted date string
 */
export function formatForDisplay(date, format = 'short') {
  try {
    // Convert to Date object
    let dateObj;
    // Check if it's a Firestore Timestamp-like object with _seconds
    if (date?._seconds !== undefined) {
      dateObj = new Date(date._seconds * 1000);
      logger.debug('[DateService] formatForDisplay: Converting Firestore timestamp with _seconds', { 
        seconds: date._seconds,
        format
      });
    } else if (date instanceof Timestamp || date?.toDate) {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string' || typeof date === 'number') {
      dateObj = new Date(date);
    } else {
      logger.warn('[DateService] formatForDisplay: Invalid date', {
        date,
        type: typeof date,
        format
      });
      return '';
    }
    
    // Check for invalid date
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    // Predefined formats
    const formats = {
      short: { month: 'short', day: 'numeric' }, // "Jan 15"
      long: { month: 'long', day: 'numeric', year: 'numeric' }, // "January 15, 2024"
      full: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }, // "Monday, January 15, 2024"
      iso: null // Special case for ISO format
    };
    
    // Handle ISO format specially
    if (format === 'iso') {
      return convertTimestampToISO(dateObj);
    }
    
    // Use predefined or custom format
    const options = typeof format === 'string' ? formats[format] || formats.short : format;
    
    return dateObj.toLocaleDateString('en-US', options);
  } catch (error) {
    logger.error('[DateService] Error formatting date:', {
      error: error.message,
      date,
      format
    });
    return '';
  }
}

/**
 * Check if two dates are the same day (ignoring time)
 * @param {Date} date1 
 * @param {Date} date2 
 * @returns {Boolean}
 */
export function isSameDay(date1, date2) {
  try {
    const d1 = date1 instanceof Date ? date1 : new Date(date1);
    const d2 = date2 instanceof Date ? date2 : new Date(date2);
    
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  } catch (error) {
    logger.error('[DateService] Error comparing dates:', {
      error: error.message,
      date1,
      date2
    });
    return false;
  }
}

